import { useState } from 'react';

import Box from '@mui/material/Box';

import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import { composioToolkitLogoUrl } from '@erikdakoda/connection/shared/composioToolkitLogoUrl';
import { useLayoutSettings } from '@erikdakoda/site-template-ui/hooks/useLayoutSettings';

export type ToolkitLogoProps = {
  readonly toolkitSlug: string;
  readonly size?: number;
  readonly alt?: string;
};

/**
 * Toolkit brand mark from Composio's CDN, falling back to a generic glyph on load failure.
 * Uses the CDN dark asset when layout mode is dark (`?theme=dark`).
 * @returns Plain logo image or fallback icon — no Avatar chrome.
 */
export default function ToolkitLogo({ toolkitSlug, size = 40, alt }: ToolkitLogoProps) {
  'use memo';

  const { settings } = useLayoutSettings();
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const slug = toolkitSlug.trim().toLowerCase();
  const logoTheme = settings.mode === 'dark' ? 'dark' : undefined;
  const src = composioToolkitLogoUrl(slug, logoTheme);
  const failed = failedSrc === src;

  if (failed || !slug) {
    return (
      <Box
        className="toolkit-logo"
        sx={{
          width: size,
          height: size,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <FaIconUvilo icon="puzzle-piece" prefix="fat" />
      </Box>
    );
  }

  return (
    <Box
      className="toolkit-logo"
      component="img"
      src={src}
      alt={alt ?? slug}
      onError={() => setFailedSrc(src)}
      sx={{ width: size, height: size, objectFit: 'contain', display: 'block' }}
    />
  );
}
